import { ApolloLink } from "@apollo/client";
import { Observable } from "@apollo/client/utilities";
import { commissionCatalogMockResults, commissionVariableMockResolvers } from "./apolloMocks";

const COMMISSION_MOCK_OPERATIONS = new Set([
  ...Object.keys(commissionCatalogMockResults),
  ...Object.keys(commissionVariableMockResolvers)
]);

/**
 * Single source of truth for whether the commission GraphQL mock link is on.
 * Fixture reads outside Apollo (e.g. person GRP IAA summaries) must use this
 * so fake data never renders in UAT or production.
 */
export function isCommissionMocksEnabled(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_COMMISSION_GRAPHQL_MOCKS === "true";
}

/**
 * Dev-only Apollo link that returns schema-aligned commission catalog fixtures
 * when VITE_COMMISSION_GRAPHQL_MOCKS=true. Non-matching operations pass through.
 */
export function createCommissionMockLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const operationName = operation.operationName;
    const variableResolver = commissionVariableMockResolvers[operationName];
    const staticData = commissionCatalogMockResults[operationName];

    if (!variableResolver && !staticData) {
      return forward(operation);
    }

    const mockData = variableResolver ? variableResolver(operation.variables as Record<string, unknown>) : staticData;

    return new Observable((observer) => {
      observer.next({ data: mockData });
      observer.complete();
    });
  });
}

export function isCommissionCatalogOperation(operationName: string): boolean {
  return COMMISSION_MOCK_OPERATIONS.has(operationName);
}
